import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../app/store';
import { v4 as uuidv4 } from 'uuid';
import { DEVICE, DeviceType } from '../constants/deviceType';

export interface SettingsState {
  socketConnected: boolean;
  socketAddress: string;
  deviceType: DeviceType;
  deviceId: number;
  uuid: string;
  visualEffects: boolean;
  forceReconnectOnLogin: boolean;
  monitorInitialDelay?: number; // ms before scroll starts
  monitorScrollSpeed?: number; // ms per scroll step
  monitorPostScrollDelay?: number; // ms after scroll before rotate
  monitorFontFamily?: string; // Font family for Monitor and MC
  selectedHall?: number | 'all'; // Which hall this device is assigned to (default: 'all')
  sendVisualControl?: boolean; // Whether this device auto-sends heats to LED/Broadcast screens
  connectionError?: string; // Last user-facing socket connection error (empty when connected), or SECURE_CONNECTION_FAILED
  // Size (px) of the online-results QR shown on Monitor. Needs to be tuned to
  // the screen and viewing distance: a size that scans fine on a laptop is
  // unreadable from the back of a hall. 0 hides it.
  monitorQrSize?: number;
}

const initialState: SettingsState = {
  socketConnected: false,
  socketAddress: '',
  deviceType: DEVICE.JUDGE,
  deviceId: 0,
  visualEffects: true,
  forceReconnectOnLogin: false,
  uuid: uuidv4(),
  monitorInitialDelay: 6000,
  monitorScrollSpeed: 60,
  monitorPostScrollDelay: 6000,
  monitorFontFamily: 'Roboto Condensed Variable, sans-serif',
  selectedHall: 'all',
  sendVisualControl: false,
  connectionError: '',
  monitorQrSize: 180,
};

export const clearAll = createAsyncThunk(
  'settings/clear',
  async function (_payload, thunkAPI) {
    thunkAPI.dispatch({ type: 'CLEAR_ALL' });
    // console.log('cleared state');
  }
);

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    changeSocketConnected(state, action: PayloadAction<boolean>) {
      state.socketConnected = action.payload;
    },
    changeSocketAddress(state, action: PayloadAction<string>) {
      state.socketAddress = action.payload;
    },
    changeDeviceType(state, action: PayloadAction<DeviceType>) {
      state.deviceType = action.payload;
    },
    changeDeviceId(state, action: PayloadAction<number>) {
      state.deviceId = action.payload;
    },
    changeVisualEffects(state, action: PayloadAction<boolean>) {
      state.visualEffects = action.payload;
    },
    changeForceReconnectOnLogin(state, action: PayloadAction<boolean>) {
      state.forceReconnectOnLogin = action.payload;
    },
    changeMonitorInitialDelay(state, action: PayloadAction<number>) {
      state.monitorInitialDelay = action.payload;
    },
    changeMonitorScrollSpeed(state, action: PayloadAction<number>) {
      state.monitorScrollSpeed = action.payload;
    },
    changeMonitorPostScrollDelay(state, action: PayloadAction<number>) {
      state.monitorPostScrollDelay = action.payload;
    },
    changeMonitorFontFamily(state, action: PayloadAction<string>) {
      state.monitorFontFamily = action.payload;
    },
    changeSelectedHall(state, action: PayloadAction<number | 'all'>) {
      state.selectedHall = action.payload;
    },
    changeSendVisualControl(state, action: PayloadAction<boolean>) {
      state.sendVisualControl = action.payload;
    },
    changeConnectionError(state, action: PayloadAction<string>) {
      state.connectionError = action.payload;
    },
    changeMonitorQrSize(state, action: PayloadAction<number>) {
      state.monitorQrSize = action.payload;
    }
  }
});

// Export actions
export const {
  changeSocketAddress,
  changeDeviceType,
  changeDeviceId,
  changeSocketConnected,
  changeVisualEffects,
  changeForceReconnectOnLogin,
  changeMonitorInitialDelay,
  changeMonitorScrollSpeed,
  changeMonitorPostScrollDelay,
  changeMonitorFontFamily,
  changeSelectedHall,
  changeSendVisualControl,
  changeConnectionError,
  changeMonitorQrSize
} = settingsSlice.actions;

//selectors
export const selectSocketConnected = (state: RootState) => {
  // console.log(state);
  return state.settings.socketConnected;
};
export const selectConnectionError = (state: RootState) => {
  return state.settings.connectionError || '';
};

// A failed wss:// connection. The browser never says why, so a certificate the
// device rejects looks exactly like a desktop that cannot be reached; Setup
// shows a translated message naming both causes instead of guessing one.
export const SECURE_CONNECTION_FAILED = 'secure-connection-failed';
export const selectSocketAddress = (state: RootState) => {
  if (state.settings.socketAddress) {
    return state.settings.socketAddress;
  }

  // Default to the same host the app is served from. In a secure (https)
  // context the browser blocks plain ws://, so target wss:// on 3000; otherwise
  // use http:// on 3001. A stored address (Setup/QR scan) always takes priority.
  const hostname = window.location.hostname;
  const isSecure =
    typeof window !== 'undefined' && window.location.protocol === 'https:';
  return isSecure ? `https://${hostname}:3000` : `http://${hostname}:3001`;
};
export const selectDeviceType = (state: RootState) => {
  return state.settings.deviceType;
};
export const selectIsTrainingMode = (state: RootState) => {
  return state.settings.deviceType === DEVICE.TRAINING;
};
export const selectDeviceId = (state: RootState) => {
  return state.settings.deviceId;
};
export const selectDeviceUuid = (state: RootState) => {
  return state.settings.uuid;
};
export const selectVisualEffects = (state: RootState) => {
  return state.settings.visualEffects;
};
export const selectForceReconnectOnLogin = (state: RootState) => {
  return state.settings.forceReconnectOnLogin;
};
export const selectMonitorInitialDelay = (state: RootState) => state.settings.monitorInitialDelay ?? 6000;
export const selectMonitorScrollSpeed = (state: RootState) => state.settings.monitorScrollSpeed ?? 60;
export const selectMonitorPostScrollDelay = (state: RootState) => state.settings.monitorPostScrollDelay ?? 6000;
export const selectMonitorFontFamily = (state: RootState) => state.settings.monitorFontFamily ?? 'Roboto Condensed Variable, sans-serif';
export const selectMonitorQrSize = (state: RootState) => state.settings.monitorQrSize ?? 180;
export const selectSelectedHall = (state: RootState) => state.settings.selectedHall ?? 'all';
export const selectSendVisualControl = (state: RootState) => state.settings.sendVisualControl ?? false;
// Export reducer
export default settingsSlice.reducer;
